/**
 * Bambu PROJECT .3mf exporter — honour-test writer + T5 paint_supports fix.
 */
import { pack3mfFromConfig } from 'bambu-3mf';
import { buildProcessSettings, P1S, SETTINGS } from '../settings/schema';
import type { PlacedObject } from '../store/types';

export interface ExportOptions {
  projectSettingsText: string;
  settings: Record<string, string | number | boolean>;
  objects: PlacedObject[];
  projectName: string;
  thumbnailPng?: Uint8Array;
  printSettingsId?: string;
  filamentSettingsId?: string;
  printerSettingsId?: string;
}

function hasPaint(objects: PlacedObject[]): boolean {
  return objects.some(
    (o) => o.paintSupports && o.paintSupports.some((v) => v === 1 || v === 2)
  );
}

function applyTransforms(
  positions: Float32Array,
  rot: [number, number, number],
  scale: [number, number, number]
): Float32Array {
  const [sx, sy, sz] = scale;
  const [rx, ry, rz] = rot;
  const cx = Math.cos(rx),
    sx_ = Math.sin(rx);
  const cy = Math.cos(ry),
    sy_ = Math.sin(ry);
  const cz = Math.cos(rz),
    sz_ = Math.sin(rz);
  // R = Rz * Ry * Rx
  const out = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    let x = positions[i] * sx;
    let y = positions[i + 1] * sy;
    let z = positions[i + 2] * sz;
    // Rx
    let y1 = y * cx - z * sx_;
    let z1 = y * sx_ + z * cx;
    y = y1;
    z = z1;
    // Ry
    let x2 = x * cy + z * sy_;
    z1 = -x * sy_ + z * cy;
    x = x2;
    z = z1;
    // Rz
    const x3 = x * cz - y * sz_;
    y1 = x * sz_ + y * cz;
    out[i] = x3;
    out[i + 1] = y1;
    out[i + 2] = z;
  }
  return out;
}

function dropToBed(positions: Float32Array): { pos: Float32Array; minY: number } {
  let minY = Infinity;
  for (let i = 1; i < positions.length; i += 3) minY = Math.min(minY, positions[i]);
  if (!Number.isFinite(minY)) minY = 0;
  const pos = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    pos[i] = positions[i];
    pos[i + 1] = positions[i + 1] - minY;
    pos[i + 2] = positions[i + 2];
  }
  return { pos, minY };
}

function bbox(positions: Float32Array) {
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    minX = Math.min(minX, positions[i]);
    maxX = Math.max(maxX, positions[i]);
    minY = Math.min(minY, positions[i + 1]);
    maxY = Math.max(maxY, positions[i + 1]);
    minZ = Math.min(minZ, positions[i + 2]);
    maxZ = Math.max(maxZ, positions[i + 2]);
  }
  return { minX, minY, minZ, maxX, maxY, maxZ };
}

function paintArray(o: PlacedObject, triCount: number): number[] | undefined {
  if (!o.paintSupports) return undefined;
  const arr = new Array(triCount);
  for (let i = 0; i < triCount; i++) arr[i] = o.paintSupports[i] || 0;
  if (!arr.some((v) => v === 1 || v === 2)) return undefined;
  return arr;
}

function perObjectProcess(o: PlacedObject): Record<string, unknown> | undefined {
  const keys = Object.keys(o.overrides);
  if (!keys.length) return undefined;
  const out: Record<string, unknown> = {};
  for (const id of keys) {
    const def = SETTINGS.find((s) => s.id === id);
    if (!def || !def.perObject) continue;
    let v: unknown = o.overrides[id];
    if (def.asPercentString) v = `${v}%`;
    else if (def.type === 'boolean') v = Boolean(v);
    out[def.key] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Build a BambuStudio-compatible PROJECT .3mf (not gcode.3mf).
 */
export function exportProject3mf(opts: ExportOptions): Uint8Array {
  const painted = hasPaint(opts.objects);
  const processSettings = buildProcessSettings(opts.settings) as Record<string, unknown>;
  if (opts.printSettingsId) processSettings.print_settings_id = opts.printSettingsId;
  if (opts.filamentSettingsId) processSettings.filament_settings_id = [opts.filamentSettingsId];
  if (opts.printerSettingsId) processSettings.printer_settings_id = opts.printerSettingsId;

  // T5 fix: painted supports need enable_support=1 + tree(manual)
  if (painted) {
    processSettings.enable_support = true;
    const st = String(processSettings.support_type ?? '');
    if (!st.includes('manual')) {
      processSettings.support_type = st.startsWith('normal')
        ? 'normal(manual)'
        : 'tree(manual)';
    }
  }

  // Plate placement: bambu-3mf uses bedSize + instances translate.
  // We bake rotation/scale into geometry and pass translate as X/Z on bed, Y=0.
  const packedObjects = opts.objects.map((o) => {
    let transformed = applyTransforms(o.positions, o.rotation, o.scale);
    const dropped = dropToBed(transformed);
    transformed = dropped.pos;
    const bb = bbox(transformed);
    const cx = (bb.minX + bb.maxX) / 2;
    const cz = (bb.minZ + bb.maxZ) / 2;
    // Centre mesh at origin in XZ; place via translate
    for (let i = 0; i < transformed.length; i += 3) {
      transformed[i] -= cx;
      transformed[i + 2] -= cz;
    }
    const triCount = transformed.length / 9;
    const supports = paintArray(o, triCount);
    const process = perObjectProcess(o);

    return {
      name: o.name,
      geometry: { position: transformed },
      // Plate: X right, Y depth on bed → Y-up XZ. Honour-tests used instances.translate.
      instances: [{ translate: { x: o.position[0], z: -o.position[1] } }],
      extruder: o.extruder + 1, // 1-based
      ...(supports ? { paint: { supports } } : {}),
      ...(process ? { settings: process as Record<string, string | number | boolean | string[]> } : {}),
    };
  });

  const zip = pack3mfFromConfig(
    { projectSettings: opts.projectSettingsText },
    packedObjects as Parameters<typeof pack3mfFromConfig>[1],
    {
      title: opts.projectName || 'P1S Project',
      designer: 'P1S Slicer',
      description: 'Client-side Bambu project 3MF for MakerWorld / Handy',
      application: 'BambuStudio-01.10.01.50',
    },
    {
      bedSize: { x: P1S.bedX, y: P1S.bedY },
      gap: 8,
      processSettings: processSettings as never,
      ...(opts.thumbnailPng
        ? { thumbnails: { middle: opts.thumbnailPng } }
        : {}),
    }
  );

  return zip;
}

/** Trigger iOS-friendly download / share of a .3mf blob */
export async function shareOrDownload3mf(
  data: Uint8Array,
  filename: string
): Promise<'shared' | 'downloaded'> {
  const blob = new Blob([data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer], { type: 'model/3mf' });
  const file = new File([blob], filename, { type: 'model/3mf' });

  if (
    typeof navigator !== 'undefined' &&
    navigator.canShare &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({ files: [file], title: filename });
      return 'shared';
    } catch (e) {
      // user cancelled or share failed → fall through
      if ((e as Error).name === 'AbortError') throw e;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return 'downloaded';
}
